import React from 'react';
import type { MsThread } from '../../messages/types';
import { Icon } from '../Icon/Icon';
import { MessagesTopicChip } from './MessagesTopicChip';

export interface MessagesConvRowProps {
  thread: MsThread;
  active: boolean;
  onClick: () => void;
}

/** Inbox row — matches reference `ConvRow`. */
export function MessagesConvRow({ thread: conv, active, onClick }: MessagesConvRowProps) {
  const emergency = conv.currentTopic === 'emergency';

  return (
    <div
      className={`ms-conv${active ? ' active' : ''}${emergency ? ' emergency' : ''}`}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      role="button"
      tabIndex={0}
    >
      <div className="ms-conv-hd">
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {emergency ? <span className="ms-emerg-dot" style={{ alignSelf: 'center' }} /> : null}
            <span
              style={{
                fontSize: 13.5,
                fontWeight: conv.unread ? 700 : 600,
                color: 'var(--ink-0)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                minWidth: 0,
              }}
            >
              {conv.name}
            </span>
            {conv.lang === 'Spanish' ? (
              <span
                style={{
                  fontSize: 9,
                  fontWeight: 700,
                  color: 'var(--ai-3)',
                  background: 'var(--ai-tint)',
                  padding: '1px 5px',
                  borderRadius: 4,
                  flex: 'none',
                }}
              >
                ES
              </span>
            ) : null}
          </div>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10.5, color: 'var(--ink-3)', marginTop: 2 }}>
            {conv.phone}
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 5, flex: 'none' }}>
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10.5, color: 'var(--ink-4)' }}>{conv.when}</span>
          {conv.unread > 0 ? (
            <span
              style={{
                minWidth: 18,
                height: 18,
                padding: '0 5px',
                borderRadius: 9,
                background: 'var(--terracotta)',
                color: '#fff',
                fontSize: 10.5,
                fontWeight: 700,
                fontFamily: 'var(--font-mono)',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {conv.unread}
            </span>
          ) : null}
        </div>
      </div>
      <div className="ms-conv-bd">
        <div
          style={{
            fontSize: 12.5,
            color: conv.unread ? 'var(--ink-1)' : 'var(--ink-2)',
            lineHeight: 1.5,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
          }}
        >
          {conv.preview}
        </div>
        {conv.previewTranslated ? (
          <div
            style={{
              fontSize: 11.5,
              color: 'var(--ink-4)',
              marginTop: 3,
              fontStyle: 'italic',
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            <Icon kind="translate" size={10} />
            {conv.previewTranslated}
          </div>
        ) : null}
        <div style={{ display: 'flex', gap: 5, marginTop: 9, flexWrap: 'wrap', alignItems: 'center' }}>
          <MessagesTopicChip topic={conv.currentTopic ?? null} sm />
          {conv.triage ? (
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 3,
                padding: '1px 7px',
                borderRadius: 'var(--r-sm)',
                background: 'var(--gold-tint)',
                color: 'var(--gold)',
                fontSize: 10,
                fontWeight: 600,
              }}
            >
              <Icon kind="alert" size={9} stroke={2.4} />
              Triage
            </span>
          ) : null}
          {!conv.autoRespondOn && !conv.optedOut ? (
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 3,
                padding: '1px 7px',
                borderRadius: 'var(--r-sm)',
                background: 'var(--cream-0)',
                color: 'var(--ink-3)',
                fontSize: 10,
                fontWeight: 600,
              }}
            >
              <Icon kind="pause" size={8} stroke={2.4} />
              Auto off
            </span>
          ) : null}
          {conv.optedOut ? (
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 3,
                padding: '1px 7px',
                borderRadius: 'var(--r-sm)',
                background: 'var(--terracotta-tint)',
                color: 'var(--terracotta)',
                fontSize: 10,
                fontWeight: 600,
              }}
            >
              <Icon kind="shield-off" size={9} stroke={2.2} />
              Opted out
            </span>
          ) : null}
        </div>
      </div>
    </div>
  );
}
